import { useState } from "react";
import { toast } from "sonner";
import { Search, X, Paperclip, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/Field";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

const when = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
const inputCls = "h-10 bg-white text-slate-900";

function Highlight({ text, word }) {
  if (!word) return text;
  const i = text.toLowerCase().indexOf(word.toLowerCase());
  if (i < 0) return text;
  const start = Math.max(0, i - 60);
  return <>{start > 0 && "…"}{text.slice(start, i)}<mark className="rounded bg-amber-200 px-0.5">{text.slice(i, i + word.length)}</mark>{text.slice(i + word.length, i + word.length + 120)}</>;
}

export function MessageSearch({ onOpen, onClose }) {
  const { churches } = useApp();
  const [f, setF] = useState({ q: "", church_id: "", start: "", end: "" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));

  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const params = { ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), tz: new Date().getTimezoneOffset() };
      setRes((await api.get("/messages/search", { params })).data);
    } catch (err) { toast.error(errMsg(err), { id: "msg-search-error" }); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="message-search">
      <form onSubmit={run} className="flex flex-wrap items-end gap-3 border-b border-slate-200 p-4">
        <label className="min-w-48 flex-1 text-xs font-semibold text-slate-500">Word or phrase
          <Input value={f.q} onChange={set("q")} maxLength={100} placeholder="e.g. receipt, conference, June report" className={`${inputCls} mt-1`} data-testid="msg-search-q" />
        </label>
        <label className="w-full text-xs font-semibold text-slate-500 sm:w-56">Church
          <div className="mt-1"><NativeSelect value={f.church_id} onChange={set("church_id")} placeholder="All churches" options={churches.map((c) => ({ value: c.id, label: c.name }))} testid="msg-search-church" /></div>
        </label>
        <label className="text-xs font-semibold text-slate-500">From<Input type="date" value={f.start} max={f.end || undefined} onChange={set("start")} className={`${inputCls} mt-1`} data-testid="msg-search-start" /></label>
        <label className="text-xs font-semibold text-slate-500">To<Input type="date" value={f.end} min={f.start || undefined} onChange={set("end")} className={`${inputCls} mt-1`} data-testid="msg-search-end" /></label>
        <Button type="submit" disabled={busy} data-testid="msg-search-submit">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />} Search</Button>
        <Button type="button" variant="ghost" onClick={onClose} data-testid="msg-search-close"><X className="mr-1 h-4 w-4" /> Close</Button>
      </form>
      {res && (
        <div className="max-h-[60vh] overflow-y-auto" data-testid="msg-search-results">
          <p className="px-4 py-2 text-xs font-semibold text-slate-500" data-testid="msg-search-count">{res.results.length}{res.more ? "+" : ""} message{res.results.length === 1 ? "" : "s"} found{res.more ? " (showing the newest 200)" : ""}</p>
          {res.results.map((r) => (
            <button key={r.id} onClick={() => onOpen(r)} className="block w-full border-t border-slate-100 px-4 py-3 text-left hover:bg-rose-50" data-testid={`msg-search-result-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                <span><span className="font-semibold text-slate-800">{r.name}</span> · {r.church_name}</span>
                <span>{when(r.created_at)}</span>
              </div>
              <div className="mt-1 text-sm text-slate-700">
                <span className="font-semibold">{r.from_admin ? `${r.sender_name} (admin)` : r.sender_name}: </span>
                {r.body ? <Highlight text={r.body} word={f.q.trim()} /> : <span className="italic text-slate-500">Attachment</span>}
              </div>
              {r.attachments.length > 0 && <div className="mt-1 flex items-center gap-1 text-xs text-slate-500"><Paperclip className="h-3 w-3" />{r.attachments.join(", ")}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
